import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'src/components/design-system/modal';
import { Checkbox, Button, message } from 'antd';
import classNames from 'classnames';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import { Lang } from 'shared/constants';
import {
  getAllKnowledgeItemIdsUnderCategory,
  hasExternalSyncDescendantById,
} from 'shared/store/selectors/knowledge.selector';
import { EXTRACTABLE_KNOWLEDGE_TYPES, isPolicyTechSynced } from 'shared/utils/knowledge.utils';
import { formatNumber } from 'shared/utils';
import { Tooltip } from 'src/components/core';
import { METADATA_FIELDS, SYNC_LOCKED_METADATA_FIELDS } from './constants';
import styles from './metadata-extraction-modal.module.scss';

const lang = Lang.METADATA_EXTRACTION;

export const MetadataExtractionModal = ({
  open,
  onClose,
  knowledgeItemIds: propKnowledgeItemIds,
  categoryId,
  categoryTitle,
}) => {
  const [selectedFields, setSelectedFields] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  const categoriesById = useSelector(state => state.knowledge.categoriesById);

  // The ids of every item below the category come from the tree in the store; the
  // backend keeps only the extractable types among them.
  const knowledgeItemIds = useMemo(() => {
    if (propKnowledgeItemIds) {
      return propKnowledgeItemIds;
    }
    if (categoryId && open) {
      return getAllKnowledgeItemIdsUnderCategory({ categoriesById, categoryId });
    }
    return [];
  }, [propKnowledgeItemIds, categoryId, categoriesById, open]);

  const hasPolicyTechContent = useSelector(
    hasExternalSyncDescendantById(categoryId ? [categoryId] : [], isPolicyTechSynced),
  );

  const isFieldLocked = useCallback(
    fieldId => hasPolicyTechContent && SYNC_LOCKED_METADATA_FIELDS.has(fieldId),
    [hasPolicyTechContent],
  );

  const selectableFieldIds = useMemo(
    () => METADATA_FIELDS.filter(({ id }) => !isFieldLocked(id)).map(({ id }) => id),
    [isFieldLocked],
  );

  const handleFieldToggle = useCallback(
    fieldId => {
      if (isFieldLocked(fieldId)) return;
      setSelectedFields(prev => {
        const next = new Set(prev);
        if (next.has(fieldId)) {
          next.delete(fieldId);
        } else {
          next.add(fieldId);
        }
        return next;
      });
    },
    [isFieldLocked],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedFields(new Set(selectableFieldIds));
  }, [selectableFieldIds]);

  const handleDeselectAll = useCallback(() => {
    setSelectedFields(new Set());
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedFields.size === 0 || knowledgeItemIds.length === 0) return;

    setLoading(true);
    try {
      const response = await knowledgeServiceApi.queueMetadataExtraction({
        knowledgeItemIds,
        types: [...EXTRACTABLE_KNOWLEDGE_TYPES],
        fields: Array.from(selectedFields),
      });

      // The Executer resolves an envelope and never throws on an HTTP error.
      if (response?.error) {
        message.error(response.data?.message || lang.ERROR_MESSAGE);
        return;
      }

      const tasksCreated = response?.data?.tasksCreated;
      if (tasksCreated !== undefined) {
        message.success(lang.SUCCESS_MESSAGE.replace('{count}', formatNumber(tasksCreated)));
      } else {
        message.success(lang.SUCCESS_MESSAGE_DEFAULT);
      }

      onClose();
    } catch (error) {
      console.error('Failed to queue metadata extraction:', error);
      message.error(error?.response?.data?.message || error?.message || lang.ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [selectedFields, knowledgeItemIds, onClose]);

  const handleCancel = useCallback(() => {
    setSelectedFields(new Set());
    onClose();
  }, [onClose]);

  const totalCount = knowledgeItemIds?.length || 0;

  // `totalCount` is every item below the category; the backend keeps only the
  // extractable types, so this is a ceiling and the success toast has the real count.
  const getItemCountText = () => {
    if (categoryTitle) {
      return lang.ITEM_COUNT_UP_TO_WITH_TITLE.replace('{count}', formatNumber(totalCount)).replace(
        '{title}',
        categoryTitle,
      );
    }
    return lang.ITEM_COUNT_UP_TO.replace('{count}', formatNumber(totalCount));
  };

  return (
    <Modal
      open={open}
      title={lang.MODAL_TITLE}
      onCancel={handleCancel}
      afterOpenChange={isOpen => {
        if (!isOpen) {
          setSelectedFields(new Set());
          setLoading(false);
        }
      }}
      onSubmit={handleSubmit}
      cancelText={lang.CANCEL_BUTTON}
      submitText={lang.SUBMIT_BUTTON}
      loading={loading}
      submitDisabled={selectedFields.size === 0 || totalCount === 0 || loading}
      width={600}>
      <div className={styles.headerActions}>
        <span className={styles.fieldCount}>
          {lang.FIELDS_SELECTED.replace('{selected}', selectedFields.size).replace(
            '{total}',
            selectableFieldIds.length,
          )}
        </span>
        <div className={styles.actionButtons}>
          <Button type="link" onClick={handleSelectAll} className={styles.actionButton}>
            {lang.SELECT_ALL}
          </Button>
          <Button type="link" onClick={handleDeselectAll} className={styles.actionButton}>
            {lang.DESELECT_ALL}
          </Button>
        </div>
      </div>

      <div className={styles.fieldsContainer}>
        {METADATA_FIELDS.map(({ id, label, description }) => {
          const locked = isFieldLocked(id);
          const field = (
            <div
              key={id}
              className={classNames(styles.fieldItem, {
                [styles.fieldItemDisabled]: locked,
              })}>
              <Checkbox
                checked={selectedFields.has(id)}
                disabled={locked}
                onChange={() => handleFieldToggle(id)}
              />
              <div className={styles.fieldContent}>
                <span className={styles.fieldLabel}>{label}</span>
                <span className={styles.fieldDescription}>{description}</span>
              </div>
            </div>
          );

          return locked ? (
            <Tooltip
              key={id}
              title={lang.FIELD_LOCKED_BY_SYNC}
              placement="topLeft"
              arrow={{ pointAtCenter: false }}>
              {field}
            </Tooltip>
          ) : (
            field
          );
        })}
      </div>

      <p className={styles.itemCount}>{getItemCountText()}</p>
    </Modal>
  );
};
